import { StorageResolver } from '@/firebase/firestore/storage-resolver';
import { normalizeDates } from '@/lib/date-utils';
import type { Novel, NovelSummaries } from '@/lib/types';
import { ENTITY_TYPES, NARRATIVE_TYPES } from '@/lib/types';
import { Firestore, collection, doc, onSnapshot } from 'firebase/firestore';

export class FirebaseNovelSubscriptions {
  constructor(private firestore: Firestore) { }

  subscribeNovel(
    ownerId: string,
    novelId: string,
    onUpdate: (novel: Novel | null) => void,
    onError: (error: Error) => void
  ): () => void {
    let coreNovel: Novel | null = null;
    const splitMetas: Record<string, any> = {};
    const subListeners: Record<string, () => void> = {};

    const resolver = new StorageResolver(this.firestore);
    const coreRef = resolver.getNovelRef(ownerId, novelId);

    const rebuildAndEmit = () => {
      if (!coreNovel) {
        onUpdate(null);
        return;
      }

      const layout = coreNovel.storageLayout;
      let allLoaded = true;

      // Check Entities Meta
      ENTITY_TYPES.forEach(type => {
        const isSplit = layout?.entities?.[type]?.meta === 'split';
        if (isSplit && !(type in splitMetas)) {
          allLoaded = false;
        }
      });

      // Check Narrative Meta
      NARRATIVE_TYPES.forEach(type => {
        const isSplit = layout?.narrative?.[type]?.meta === 'split';
        if (isSplit && !(type in splitMetas)) {
          allLoaded = false;
        }
      });

      if (!allLoaded) return; // Wait until all split docs are fetched

      // Merge summaries
      const summaries: Record<string, Record<string, any>> = {};
      // Preserve dynamic embedded settings keys (for example
      // `codex:religions`) in addition to the fixed entity types below.
      // Rebuilding this map from ENTITY_TYPES alone silently dropped custom
      // Codex category settings from the client-side Novel model.
      const settings: Record<string, any[]> = {
        ...(coreNovel.entitiesMeta?.settings || {}),
      };

      // Merge Entities
      ENTITY_TYPES.forEach(type => {
        const isSplit = layout?.entities?.[type as keyof typeof layout.entities]?.meta === 'split';
        const metaDoc = isSplit ? splitMetas[type] : null;

        summaries[type] = isSplit
          ? (metaDoc?.summaries || {})
          : (coreNovel?.entitiesMeta?.summaries?.[type] || {});

        settings[type] = isSplit
          ? (metaDoc?.settings || [])
          : (coreNovel?.entitiesMeta?.settings?.[type] || []);
      });

      // Merge Narrative
      NARRATIVE_TYPES.forEach(type => {
        const isSplit = layout?.narrative?.[type]?.meta === 'split';
        const metaDoc = isSplit ? splitMetas[type] : null;

        summaries[type] = isSplit
          ? (metaDoc?.summaries || {})
          : (coreNovel?.narrativeMeta?.summaries?.[type] || {});
      });

      const fullNovel: Novel = {
        ...coreNovel,
        summaries: summaries as NovelSummaries,
        settings,
        noteFolders: coreNovel.noteFolders || {},
        noteFolderParents: coreNovel.noteFolderParents || {},
        codexCategories: coreNovel.entitiesMeta?.codexCategories || {},
        codexCategoryParents: coreNovel.entitiesMeta?.codexCategoryParents || {},
      };

      onUpdate(normalizeDates(fullNovel));
    };

    const unsubscribeCore = onSnapshot(coreRef, (snapshot) => {
      if (!snapshot.exists()) {
        coreNovel = null;
        rebuildAndEmit();
        return;
      }

      coreNovel = { ...(snapshot.data() as Novel), id: snapshot.id };
      const layout = coreNovel.storageLayout;
      const activeKeys = new Set<string>();

      // Entities Meta
      ENTITY_TYPES.forEach(type => {
        const isSplit = layout?.entities?.[type]?.meta === 'split';
        if (isSplit) {
          const key = `entitiesMeta:${type}`;
          activeKeys.add(key);
          if (!subListeners[key]) {
            const docRef = doc(this.firestore, 'users', ownerId, 'novels', novelId, 'entitiesMeta', type);
            subListeners[key] = onSnapshot(docRef, (snap) => {
              splitMetas[type] = snap.exists() ? snap.data() : null;
              rebuildAndEmit();
            }, onError);
          }
        }
      });

      // Narrative Meta
      NARRATIVE_TYPES.forEach(type => {
        const isSplit = layout?.narrative?.[type]?.meta === 'split';
        if (isSplit) {
          const key = `narrativeMeta:${type}`;
          activeKeys.add(key);
          if (!subListeners[key]) {
            const docRef = doc(this.firestore, 'users', ownerId, 'novels', novelId, 'narrativeMeta', type);
            subListeners[key] = onSnapshot(docRef, (snap) => {
              splitMetas[type] = snap.exists() ? snap.data() : null;
              rebuildAndEmit();
            }, onError);
          }
        }
      });

      // Clean up listeners that are no longer active
      Object.keys(subListeners).forEach(key => {
        if (!activeKeys.has(key)) {
          subListeners[key]();
          delete subListeners[key];
          const type = key.split(':')[1];
          delete splitMetas[type];
        }
      });

      rebuildAndEmit();
    }, onError);

    return () => {
      unsubscribeCore();
      Object.values(subListeners).forEach(unsub => unsub());
    };
  }

  subscribeNovels(
    ownerId: string,
    onUpdate: (novels: Novel[]) => void,
    onError: (error: Error) => void
  ): () => void {
    const collRef = collection(this.firestore, 'users', ownerId, 'novels');
    return onSnapshot(
      collRef,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => normalizeDates({
          ...(doc.data() as Novel),
          id: doc.id
        }));
        onUpdate(list);
      },
      onError
    );
  }
}
